export type ProductQrCode =
  | { type: 'hid'; husqvarnaId: string }
  | {
      type: 'gs1';
      gtin: string;
      serialNumber: string;
      pnc: string | null;
    };

export type RegistrationQrCode =
  | { type: 'hid'; husqvarnaId: string }
  | { type: 'gs1'; gtin: string; serialNumber: string; pnc: string };

const DIGITAL_LINK_HOST = 'id.husqvarna.com';
const GTIN_LENGTH = 14;

/**
 * Legacy QR codes carry a Husqvarna ID in the `HID` query parameter.
 * GS1 Digital Link codes use AI 01 (GTIN), AI 21 (serial / HSN) and, during
 * the transition, AI 240 (PNC). The same HSN can be printed on a product and
 * its packaging, and it is not shared by a different product. Registration
 * still looks up PNC + serial. A code with only GTIN and serial is left for
 * SCD-2458 and is not a registration scan.
 */
export function parseProductQrCode(raw: string): ProductQrCode | null {
  const value = raw.trim();
  if (!value) {
    return null;
  }

  const url = toUrl(value);
  if (url && isDigitalLinkHost(url)) {
    return parseGs1DigitalLink(url);
  }

  return parseLegacyHid(value);
}

export function parseRegistrationQrCode(
  raw: string
): RegistrationQrCode | null {
  const parsed = parseProductQrCode(raw);
  if (!parsed) {
    return null;
  }

  if (parsed.type === 'hid') {
    return parsed;
  }

  if (!parsed.pnc) {
    return null;
  }

  return {
    type: 'gs1',
    gtin: parsed.gtin,
    serialNumber: parsed.serialNumber,
    pnc: parsed.pnc
  };
}

function toUrl(value: string): URL | null {
  try {
    return new URL(value);
  } catch {
    return null;
  }
}

function isDigitalLinkHost(url: URL): boolean {
  return url.hostname.toLowerCase() === DIGITAL_LINK_HOST;
}

function parseGs1DigitalLink(url: URL): ProductQrCode | null {
  const [gtinAi, gtin, serialAi, serialNumber, ...rest] = url.pathname
    .split('/')
    .filter(Boolean);

  const hasGtin = !!gtin && gtin.length === GTIN_LENGTH && /^\d+$/.test(gtin);
  if (
    rest.length > 0 ||
    gtinAi !== '01' ||
    serialAi !== '21' ||
    !hasGtin ||
    !serialNumber
  ) {
    return null;
  }

  return {
    type: 'gs1',
    gtin,
    serialNumber: decodeSerial(serialNumber),
    pnc: url.searchParams.get('240')?.trim() || null
  };
}

function decodeSerial(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function parseLegacyHid(value: string): ProductQrCode | null {
  const search = value.substring(value.indexOf('?'));
  const husqvarnaId = new URLSearchParams(search).get('HID');
  if (!husqvarnaId) {
    return null;
  }

  return { type: 'hid', husqvarnaId };
}
